import * as homeV2Service from "../../../../services/HomeV2";
import * as dashboardService from "../../../../services/Dashboard";
import { toaster } from "baseui/toast";
import jobsActions from "../../jobs/jobs.actions";
import { CodeConstant } from "../../../../constants";
import { pushLog2DV2LogServer } from '../../../../services/logging';
import { getUserObject } from '../../../util/getUserFullName';
const user = getUserObject();

export const HomeV2Actions = {
    SET_TALENT_INFORMATION: "SET_TALENT_INFORMATION",
    SET_ROLE_TYPE_ID: "SET_ROLE_TYPE_ID",
    SET_COUNTRY_ID_AND_CITY: "SET_COUNTRY_ID_AND_CITY",
    SET_COUNTRIES: "SET_COUNTRIES",
    SET_JOB_ROLE_TYPES: "SET_JOB_ROLE_TYPES",
    LOADING: "LOADING",
};

const FETCH_TALENT_INFORMATION_FOR_HOME_V2 = () => async (dispatch) => {
    dispatch({ type: HomeV2Actions.LOADING, payload: true });

    try {
        const response = await homeV2Service.getTalentInformationForHomePage();
        const talentInformationForHomePage = response?.data?.data?.talentInformationForHomePage || null;
        dispatch({ type: HomeV2Actions.SET_TALENT_INFORMATION, payload: talentInformationForHomePage });
    } catch (e) {
        toaster.negative("Oops! Something went wrong while fetching user information. Try refreshing the page and trying again.", {});
    } finally {
        dispatch({ type: HomeV2Actions.LOADING, payload: false });
    }
};

const FETCH_JOB_ROLE_TYPES = (roles_to_skip) => async (dispatch) => {
    dispatch({ type: HomeV2Actions.LOADING, payload: true });

    try {
        const response = await dashboardService.getJobRoleType();
        const jobRoleTypes
            = response?.data?.result
                .filter(role => !roles_to_skip.includes(role.id)) || null;
        dispatch({ type: HomeV2Actions.SET_JOB_ROLE_TYPES, payload: jobRoleTypes });
    } catch (e) {
        toaster.negative("Oops! Something went wrong while fetching job roles. Try refreshing the page and trying again.", {});
    } finally {
        dispatch({ type: HomeV2Actions.LOADING, payload: false });
    }
};

const FETCH_COUNTRIES = () => async (dispatch) => {
    dispatch({ type: HomeV2Actions.LOADING, payload: true });

    try {
        const response = await dashboardService.getAllCountries();
        const countries = response?.data?.result.filter(country => !country.is_ofac) // Exclude OFAC countries
            .map(country => {
                return {
                    id: country.id,
                    name: country.name
                }
            }) || null;
        dispatch({ type: HomeV2Actions.SET_COUNTRIES, payload: countries });
    } catch (e) {
        toaster.negative("Oops! Something went wrong while fetching countries. Try refreshing the page and trying again.", {});
    } finally {
        dispatch({ type: HomeV2Actions.LOADING, payload: false });
    }
};

const UPDATE_JOB_ROLE_TYPE = (role_type_id) => async (dispatch) => {
    try {
        const response = await dashboardService.updateBasicInfo({ action: 'UPDATE_PREFERED_ROLE', role_type_id });

        if (response?.data?.success && response?.status === 200) {
            dispatch({ type: HomeV2Actions.SET_ROLE_TYPE_ID, payload: role_type_id });
            dispatch(jobsActions.FETCH_JOBS_V2);
            pushLog2DV2LogServer.info(  
                'HOME_PAGE_V2',
                'UPDATED_PREFERRED_ROLE',
                {
                    user_id: user.uid
                },
                {
                    role_type_id
                }
            );
            toaster.positive("Your preferred role has been successfully updated!", {});
            return Promise.resolve();
        } else {
            pushLog2DV2LogServer.error(
                'HOME_PAGE_V2',
                'FAILED_TO_UPDATE_PREFERRED_ROLE',
                {
                    user_id: user.uid
                },
                {}
            );
            toaster.negative("Oops! Something went wrong while saving your role. Try refreshing the page and trying again.", {});
            return Promise.reject();
        }
    } catch (e) {
        toaster.negative("Oops! Something went wrong while saving your role. Try refreshing the page and trying again.", {});
        pushLog2DV2LogServer.error(
            'HOME_PAGE_V2',
            'FAILED_TO_UPDATE_PREFERRED_ROLE',
            {
                user_id: user.uid
            },
            {},
            { error: e.message }
        );
        return Promise.reject();
    }
};

const UPDATE_LOCATION = (country_id, city_of_residence) => async (dispatch) => {
    try {
        const response = await dashboardService.updateBasicInfo(
            {
                action: 'UPDATE_COUNTRY_DATA',
                city_of_residence,
                country_id
            });
        if (response?.data?.success && response?.status === 200) {
            dispatch({
                type: HomeV2Actions.SET_COUNTRY_ID_AND_CITY,
                payload: {
                    countryId: country_id,
                    city: city_of_residence,
                }
            });
            dispatch(jobsActions.FETCH_JOBS_V2);
            toaster.positive("Your location has been successfully updated!", {});
            pushLog2DV2LogServer.info(
                'HOME_PAGE_V2',
                'UPDATED_LOCATION',
                {
                    user_id: user.uid
                },
                {
                    country_id,
                    city_of_residence
                }
            );
        } else {
            pushLog2DV2LogServer.error( 
                'HOME_PAGE_V2',
                'FAILED_TO_UPDATE_LOCATION',
                {
                    user_id: user.uid
                },
                {}
            );
            toaster.negative("Oops! Something went wrong while saving your location. Try refreshing the page and trying again.", {});
            return Promise.reject();
        }
    } catch (e) {
        toaster.negative("Oops! Something went wrong while saving your location. Try refreshing the page and trying again.", {});
        pushLog2DV2LogServer.error(
            'HOME_PAGE_V2',
            'FAILED_TO_UPDATE_LOCATION',
            {
                user_id: user.uid
            },
            {},
            { error: e.message }
        );
        return Promise.reject();
    }
};

const UPDATE_AVATAR = (file) => async (dispatch) => {
    dispatch({ type: HomeV2Actions.SET_TALENT_INFORMATION, payload: { loading: true } });
    try {
        const response = await dashboardService.saveAvatar(file);
        if (response?.data?.code === CodeConstant.OK) {
            const { userInfo } = response.data;
            dispatch({ type: HomeV2Actions.SET_TALENT_INFORMATION, payload: { avatar: userInfo?.avatar } });
            toaster.positive("Your avatar has been successfully updated!", {});
            pushLog2DV2LogServer.info(
                'HOME_PAGE_V2',
                'UPDATED_AVATAR',
                {
                    user_id: user.uid
                },
                {}
            );
            return Promise.resolve();
        } else {
            pushLog2DV2LogServer.error(
                'HOME_PAGE_V2',
                'FAILED_TO_UPDATE_AVATAR',
                {
                    user_id: user.uid
                },
                {},
                { error: response?.data?.message }
            );
            toaster.negative("Oops! Something went wrong while saving your avatar. Try refreshing the page and trying again.", {});
            return Promise.reject();
        }
    } catch (e) {
        pushLog2DV2LogServer.error(
            'HOME_PAGE_V2',
            'FAILED_TO_UPDATE_AVATAR',
            {
                user_id: user.uid
            },
            {},
            { error: e.message }
        );
        toaster.negative("Oops! Something went wrong while saving your avatar. Try refreshing the page and trying again.", {});
        return Promise.reject();
    } finally {
        dispatch({ type: HomeV2Actions.SET_TALENT_INFORMATION, payload: { loading: false } });
    }
}

export const HOMEV2_API_ACTIONS = {
    FETCH_TALENT_INFORMATION_FOR_HOME_V2,
    FETCH_JOB_ROLE_TYPES,
    FETCH_COUNTRIES,
    UPDATE_JOB_ROLE_TYPE,
    UPDATE_LOCATION,
    UPDATE_AVATAR
}